import React from "react"

/**
 * Home's outline icon set — one family, Lucide silhouettes, drawn here rather
 * than pulled in as a dependency so the stroke weight and viewBox match the
 * widget library's existing `kpiIconMap` glyphs exactly.
 */
type IconComponent = React.FC<React.SVGProps<SVGSVGElement>>

const Svg = ({ children, ...props }: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    focusable="false"
    {...props}
  >
    {children}
  </svg>
)

export const FolderIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </Svg>
)

export const ClockIcon: IconComponent = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
)

export const ClipboardCheckIcon: IconComponent = (props) => (
  <Svg {...props}>
    <rect x="8" y="3" width="8" height="4" rx="1" />
    <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    <path d="m9 14 2 2 4-4" />
  </Svg>
)

export const GaugeIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M4.5 18a9 9 0 1 1 15 0" />
    <path d="m12 14 4-4" />
  </Svg>
)

export const DollarSignIcon: IconComponent = (props) => (
  <Svg {...props}>
    <line x1="12" y1="2.5" x2="12" y2="21.5" />
    <path d="M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </Svg>
)

export const ReceiptTextIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M4 3h16v18l-2.5-1.6L15 21l-3-1.6L9 21l-2.5-1.6L4 21z" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </Svg>
)

export const TrendingUpIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M17 8h4v4" />
  </Svg>
)

export const TrophyIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11" />
  </Svg>
)

export const CircleAlertIcon: IconComponent = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5M12 16.2h.01" />
  </Svg>
)

export const FolderKanbanIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 11v4M12 11v2M15 11v5" />
  </Svg>
)

export const ClipboardListIcon: IconComponent = (props) => (
  <Svg {...props}>
    <rect x="8" y="3" width="8" height="4" rx="1" />
    <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    <path d="M8.5 12h.01M11.5 12H16M8.5 16h.01M11.5 16H16" />
  </Svg>
)

export const UsersIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M15.5 20v-1.5a3.5 3.5 0 0 0-3.5-3.5H7a3.5 3.5 0 0 0-3.5 3.5V20" />
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M20.5 20v-1.5a3.5 3.5 0 0 0-2.6-3.4M16 4.6a3.5 3.5 0 0 1 0 6.8" />
  </Svg>
)

export const ChartNoAxesCombinedIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M4 20V10M9 20V4M14 20v-7M19 20V8" />
  </Svg>
)

export const ChartPieIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M21 12A9 9 0 1 1 12 3v9z" />
    <path d="M16 3.5A9 9 0 0 1 20.5 8L12 12z" />
  </Svg>
)

export const ChevronRightIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="m9 6 6 6-6 6" />
  </Svg>
)

export const ArrowRightIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M4 12h15M13 6l6 6-6 6" />
  </Svg>
)

export const InfoIcon: IconComponent = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 7.8h.01" />
  </Svg>
)

/** The metric/shortcut/finance icon tile — one size knob, one tint pair. */
export const IconTile = ({
  icon: Icon,
  size,
  iconSize,
  background,
  color,
  radius = 10
}: {
  icon: IconComponent
  size: number
  iconSize: number
  background: string
  color: string
  radius?: number
}) => (
  <span
    aria-hidden
    style={{
      display: "inline-flex",
      flex: "none",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      borderRadius: radius,
      background,
      color
    }}
  >
    <Icon width={iconSize} height={iconSize} style={{ display: "block" }} />
  </span>
)
